import { useCallback, useEffect, useRef, useState } from "react";
import Card from "./Card";
import Button from "./Button";
import { enrollPasskey, fetchPasskeyStatus } from "../lib/passkey";

/**
 * SPEC-68 §4.15 — the shared passkey-enrollment card.
 *
 * Self-gating: it fetches its OWN status and renders NOTHING unless
 * `canEnroll && !hasPasskey`. Both hosts mount it unconditionally, so the
 * gating lives in exactly one place and the two role paths cannot drift.
 *
 * CEREMONY-AVAILABILITY GATE (2026-09-16 field finding, PM-approved option A;
 * corrected same day after the first fix proved insufficient):
 * the Eagle Timeclock iOS shell (SPEC-30) is a plain WKWebView. There,
 * PublicKeyCredential EXISTS (so browserSupportsWebAuthn() returns true —
 * that was the first, wrong gate) but navigator.credentials.create() is
 * REFUSED with NotAllowedError and NO OS sheet, which the card misreports as
 * "Passkey setup cancelled." The reliable shell signal is the NFC bridge
 * fingerprint: window.webkit.messageHandlers.eagleNFC exists ONLY inside our
 * app (same probe lib/nfc.ts uses). Either condition hides the card.
 * DEFERRED: native passkey support in the iOS shell — docs/deferred.md.
 *
 * No props: the driver host (WaitingScreen) and the dispatcher host
 * (DispatchHome, Phase 11) pass nothing. (`onToast` is optional and only used
 * by hosts that have a toast stack.)
 *
 * CEREMONY STATE RESET IS LOAD-BEARING (r1 BUG #11): the challengeToken is
 * single-use — the WebAuthChallengeGuard Durable Object consumes it, so a retry
 * that reuses it gets a replay 401 the driver can never clear. This component
 * keeps NO challengeToken state at all: `enrollPasskey` owns the ceremony and
 * re-runs `begin` on every call, so a retry is structurally a FRESH challenge
 * (test 49g proves the two tokens are distinct).
 */
export default function PasskeyEnrollmentCard({
  onToast,
}: {
  onToast?: (msg: string, kind?: "success" | "error") => void;
} = {}) {
  const [status, setStatus] = useState<{
    hasPasskey: boolean;
    canEnroll: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  // Guards against a state update after unmount (the card is on a polling host).
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const s = await fetchPasskeyStatus();
      if (mountedRef.current) setStatus({ hasPasskey: s.hasPasskey, canEnroll: s.canEnroll });
    } catch {
      // A failed status probe must NEVER surface as an error to a driver — the
      // card simply does not render. Offline-tolerant by design.
      if (mountedRef.current) setStatus(null);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  const onEnroll = useCallback(async () => {
    setBusy(true);
    try {
      const result = await enrollPasskey();
      if (!mountedRef.current) return;
      if (result.ok) {
        onToast?.("Passkey added — you can sign in without your PIN.", "success");
        await refresh();
      } else if (result.code === "enrollment_not_available") {
        // An office policy, not a failure: hide the card rather than showing a
        // driver an error they cannot act on.
        setStatus({ hasPasskey: false, canEnroll: false });
      } else if (result.code === "NotAllowedError") {
        // The driver dismissed the OS prompt. Stay tappable — retrying issues a
        // FRESH challenge (enrollPasskey re-runs begin), so this cannot strand.
        onToast?.("Passkey setup cancelled.", "error");
      } else {
        onToast?.(result.message || "Couldn't set up a passkey.", "error");
      }
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [onToast, refresh]);

  if (
    // The iOS shell fingerprint (SPEC-30 WKWebView bridge) — PublicKeyCredential
    // EXISTS there but the ceremony is refused; see the header comment.
    (typeof window !== "undefined" &&
      Boolean(
        (window as unknown as {
          webkit?: { messageHandlers?: { eagleNFC?: unknown } };
        }).webkit?.messageHandlers?.eagleNFC,
      )) ||
    typeof window === "undefined" ||
    !("PublicKeyCredential" in window)
  ) {
    return null;
  }
  if (!status?.canEnroll || status.hasPasskey) return null;

  return (
    <Card className="list-banner passkey-card">
      <p className="muted">
        🔐 Add a passkey to sign in faster — you just clocked in, so now is the time.
      </p>
      <Button variant="primary" disabled={busy} onClick={() => void onEnroll()}>
        {busy ? "Setting up…" : "Add a passkey"}
      </Button>
    </Card>
  );
}
